import { fmtNumber } from '../lib/reports'

/**
 * Graphiques simples du tableau de bord (ADR-036, compétence « dataviz ») :
 * - barres horizontales d'une seule série (teinte unique `--chart-1`) : la
 *   valeur est écrite à côté de chaque barre (pas de légende, le titre nomme
 *   la série) ;
 * - barre empilée de 3 catégories au plus, dans l'ordre fixe `--chart-1..3`,
 *   avec légende et valeurs écrites (jamais la couleur seule).
 * Survol : infobulle native ; lecture d'écran : liste / texte équivalents.
 */

export function BarList({
  items,
  label,
  max,
}: {
  items: { key: string; label: string; count: number }[]
  label: string
  max?: number
}) {
  const top = max ?? Math.max(1, ...items.map((i) => i.count))
  if (!items.length) return <p className="muted">Aucune donnée.</p>
  return (
    <ul className="bar-list" aria-label={label}>
      {items.map((i) => (
        <li key={i.key} title={`${i.label} : ${fmtNumber(i.count)}`}>
          <span className="bar-label">{i.label}</span>
          <span className="bar-track" aria-hidden="true">
            <span className="bar-fill" style={{ width: `${(i.count / top) * 100}%` }} />
          </span>
          <span className="bar-value">{fmtNumber(i.count)}</span>
        </li>
      ))}
    </ul>
  )
}

export interface Segment {
  key: string
  label: string
  value: number
}

const SLOTS = ['var(--chart-1)', 'var(--chart-2)', 'var(--chart-3)'] as const

export function StackedBar({ segments, label }: { segments: Segment[]; label: string }) {
  const total = segments.reduce((s, x) => s + x.value, 0)
  return (
    <figure className="stacked">
      <div
        className="stacked-bar"
        role="img"
        aria-label={`${label} : ${segments.map((s) => `${s.label} ${s.value}`).join(', ')}`}
      >
        {total === 0 ? (
          <span className="stacked-empty" />
        ) : (
          segments
            .filter((s) => s.value > 0)
            .map((s) => (
              <span
                key={s.key}
                title={`${s.label} : ${fmtNumber(s.value)}`}
                style={{
                  flexGrow: s.value,
                  background: SLOTS[segments.indexOf(s)],
                }}
              />
            ))
        )}
      </div>
      <figcaption className="stacked-legend">
        {segments.map((s, i) => (
          <span key={s.key}>
            <span className="legend-swatch" style={{ background: SLOTS[i] }} aria-hidden="true" />
            {s.label} <strong>{fmtNumber(s.value)}</strong>
          </span>
        ))}
      </figcaption>
    </figure>
  )
}

/** Jauge de taux (participation) : une barre, la valeur en texte. */
export function Meter({ value, label }: { value: number | null; label: string }) {
  if (value === null) return <span className="muted">—</span>
  return (
    <span className="meter">
      <span
        className="meter-track"
        role="meter"
        aria-label={label}
        aria-valuenow={value}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <span className="meter-fill" style={{ width: `${Math.min(100, value)}%` }} />
      </span>
      <span className="bar-value">{String(value).replace('.', ',')} %</span>
    </span>
  )
}
